<script setup lang="ts">
import HeadingNameEdit from "../HeadingNameEdit.vue";
import { nextTick, useTemplateRef } from "vue";
import { nothing } from "../../lib/maybe";
import { useTea } from "../../lib/tea/useTea";
import { updateName } from "./StoryboardName.update";
import ZwTextarea from "../ui/ZwTextarea.vue";
const props = defineProps<{
  name: string;
  prefix: string;
  label: string;
  onRename: (name: string) => void | Promise<void>;
}>();
const { model, dispatch } = useTea(
  { active: false, saving: false, draft: "" },
  updateName,
);
const input = useTemplateRef<InstanceType<typeof ZwTextarea>>("input");
async function start(): Promise<void> {
  await dispatch({ type: "Start", value: props.name });
  await nextTick();
  input.value?.focus();
}
async function commit(): Promise<void> {
  if (model.value.active === false) return;
  const name = model.value.draft.trim();
  if (name === props.name) {
    await dispatch({ type: "Close" });
    return;
  }
  await dispatch({ type: "Commit", value: name });
  try {
    await props.onRename(name);
  } finally {
    await dispatch({ type: "Close" });
  }
}
</script>
<template>
  <HeadingNameEdit
    v-if="model.active"
    :prefix="prefix.replace(/\n$/, ': ')"
    :stacked="false"
  >
    <ZwTextarea
      :id="nothing"
      ref="input"
      :label="nothing"
      variant="plain"
      :rows="1"
      autosize
      class="zw-timeline-name-editor"
      :model-value="model.draft"
      :aria-label="label"
      @update:model-value="dispatch({ type: 'Change', value: $event })"
      @keydown.enter.prevent="commit"
      @keydown.escape.prevent="dispatch({ type: 'Close' })"
      @blur="commit"
    />
  </HeadingNameEdit>
  <button
    v-else
    type="button"
    class="zw-timeline-name"
    :aria-label="`Rename ${model.saving ? model.draft : name}`"
    :disabled="model.saving"
    :aria-busy="model.saving"
    @click.stop="start"
  >
    <slot>{{ model.saving ? model.draft : name }}</slot>
  </button>
</template>
<style scoped>
.zw-timeline-name-editor,
.zw-timeline-name-editor :deep(.zw-input),
.zw-timeline-name-editor :deep(.zw-input-face),
.zw-timeline-name-editor :deep(.zw-input-face-text) {
  font: inherit;
}

.zw-timeline-name {
  border: 0;
  background: var(--bg);
  color: var(--text);
  font: inherit;
  padding: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;
  max-width: 100%;
  cursor: text;
}
</style>
